import { API_BASE } from '../config';
import { fetchWithTimeout } from '../utils/fetchWithTimeout';
import React, { useState, useEffect } from 'react';
import { ModernCard, ModernBadge, colorPalette } from './ModernPageLayout';
import { Users, Plus, Search, Filter, Edit, Trash2, CheckCircle, XCircle, Mail, Building, Home, PanelRightOpen, FileUp, Download, ChevronDown } from 'lucide-react';
import { useTranslation } from '../i18n/LanguageContext';
import LanguageSelector from './LanguageSelector';
import CustomSelect from './CustomSelect';
import { useAuth } from '../App';

// Department options: value stored in DB, labelKey for i18n
const DEPARTMENT_OPTIONS = [
  { value: 'Direction Générale', labelKey: 'employees.dep_direction_generale' },
  { value: 'Direction', labelKey: 'employees.dep_direction' },
  { value: 'Administration', labelKey: 'employees.dep_administration' },
  { value: 'Ressources Humaines', labelKey: 'employees.dep_ressources_humaines' },
  { value: 'Finance', labelKey: 'employees.dep_finance' },
  { value: 'Comptabilité', labelKey: 'employees.dep_comptabilite' },
  { value: 'Commercial', labelKey: 'employees.dep_commercial' },
  { value: 'Marketing', labelKey: 'employees.dep_marketing' },
  { value: 'Communication', labelKey: 'employees.dep_communication' },
  { value: 'Informatique', labelKey: 'employees.dep_informatique' },
  { value: 'Technique', labelKey: 'employees.dep_technique' },
  { value: 'Production', labelKey: 'employees.dep_production' },
  { value: 'Logistique', labelKey: 'employees.dep_logistique' },
  { value: 'Achats', labelKey: 'employees.dep_achats' },
  { value: 'Qualité', labelKey: 'employees.dep_qualite' },
  { value: 'R&D', labelKey: 'employees.dep_rd' },
  { value: 'Enseignement', labelKey: 'employees.dep_enseignement' },
  { value: 'Recherche', labelKey: 'employees.dep_recherche' },
  { value: 'Vie Scolaire', labelKey: 'employees.dep_vie_scolaire' },
  { value: 'Bibliothèque', labelKey: 'employees.dep_bibliotheque' },
  { value: 'Médical', labelKey: 'employees.dep_medical' },
  { value: 'Soins', labelKey: 'employees.dep_soins' },
  { value: 'Laboratoire', labelKey: 'employees.dep_laboratoire' },
  { value: 'Pharmacie', labelKey: 'employees.dep_pharmacie' },
  { value: 'Sécurité', labelKey: 'employees.dep_securite' },
  { value: 'Juridique', labelKey: 'employees.dep_juridique' },
  { value: 'Maintenance', labelKey: 'employees.dep_maintenance' },
  { value: 'Nettoyage', labelKey: 'employees.dep_nettoyage' },
  { value: 'Restauration', labelKey: 'employees.dep_restauration' },
  { value: 'Transport', labelKey: 'employees.dep_transport' },
  { value: 'BTP', labelKey: 'employees.dep_btp' },
  { value: 'Industrie', labelKey: 'employees.dep_industrie' },
  { value: 'Retail', labelKey: 'employees.dep_retail' },
  { value: 'Hôtellerie', labelKey: 'employees.dep_hotellerie' },
  { value: 'Banque', labelKey: 'employees.dep_banque' },
  { value: 'Assurance', labelKey: 'employees.dep_assurance' },
  { value: 'Consultance', labelKey: 'employees.dep_consultance' },
  { value: 'Support', labelKey: 'employees.dep_support' },
  { value: 'Polyvalent', labelKey: 'employees.dep_polyvalent' },
  { value: 'Autre', labelKey: 'employees.dep_autre' }
];

// Composant EmployeeForm intégré (garder tel quel - il fonctionne)
const EmployeeForm = ({ employee = null, onSave, onCancel, departments = [] }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    // 🚀 PHASE 1: Matricule et code override
    matricule: '',
    employeeCode: '',
    useAutoCode: true, // Par défaut génération automatique
    // Champs de base
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    department: '',
    position: '',
    hireDate: '',
    // 🚀 PHASE 3: Rémunération flexible
    hourlyRate: '',
    salaryMonthly: '',
    salaryType: 'hourly', // hourly, monthly, annual
    salaryCurrency: 'EUR', // EUR (€), USD ($), XOF (FCFA)
    // 🚀 PHASE 2: Secteur public
    administration: '',
    service: '',
    bureau: '',
    grade: '',
    echelle: '',
    indice: '',
    statut: '',
    dateTitularisation: '',
    employmentType: '',
    // Autres
    notes: '',
    isActive: true,
    createUserAccount: true, // Activé par défaut
    userPassword: 'admin123',
    userRole: 'employee' // 🚀 NOUVEAU: Par défaut employee
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (employee) {
      setFormData({
        // 🚀 PHASE 1: Matricule et code
        matricule: employee.matricule || '',
        employeeCode: employee.employeeCode || '',
        useAutoCode: false, // En mode édition, le code existe déjà
        // Champs de base
        firstName: employee.firstName || '',
        lastName: employee.lastName || '',
        email: employee.email || '',
        phone: employee.phone || '',
        department: employee.department || '',
        position: employee.position || '',
        hireDate: employee.hireDate || '',
        // 🚀 PHASE 3: Rémunération
        hourlyRate: employee.hourlyRate || '',
        salaryMonthly: employee.salaryMonthly || '',
        salaryType: employee.salaryType || 'hourly',
        salaryCurrency: employee.salaryCurrency || 'EUR',
        // 🚀 PHASE 2: Secteur public
        administration: employee.administration || '',
        service: employee.service || '',
        bureau: employee.bureau || '',
        grade: employee.grade || '',
        echelle: employee.echelle || '',
        indice: employee.indice || '',
        statut: employee.statut || '',
        dateTitularisation: employee.dateTitularisation || '',
        employmentType: employee.employmentType || '',
        // Autres
        notes: employee.notes || '',
        isActive: employee.isActive !== undefined ? employee.isActive : true,
        createUserAccount: !employee.hasUserAccount,
        userPassword: 'admin123',
        userRole: employee.userRole || 'employee'
      });
    } else {
      // Réinitialiser pour nouveau employé
      setFormData(prev => ({
        ...prev,
        matricule: '',
        employeeCode: '',
        useAutoCode: true,
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        department: '',
        position: '',
        hireDate: '',
        hourlyRate: '',
        salaryMonthly: '',
        salaryType: 'hourly',
        salaryCurrency: 'EUR',
        administration: '',
        service: '',
        bureau: '',
        grade: '',
        echelle: '',
        indice: '',
        statut: '',
        dateTitularisation: '',
        employmentType: '',
        notes: '',
        isActive: true,
        createUserAccount: true,
        userPassword: 'admin123',
        userRole: 'employee'
      }));
    }
  }, [employee]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const success = await onSave(formData);
      if (success) {
        onCancel();
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Styles modernes pour le formulaire
  const modernInputStyle = {
    width: '100%',
    padding: '12px 16px',
    background: 'rgba(255, 255, 255, 0.05)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '12px',
    color: '#ffffff',
    fontSize: '15px',
    transition: 'all 0.2s',
    fontFamily: 'inherit'
  };

  const modernLabelStyle = {
    display: 'block',
    marginBottom: '8px',
    fontWeight: 600,
    color: 'rgba(255, 255, 255, 0.9)',
    fontSize: '14px'
  };

  const modernSectionStyle = {
    marginBottom: '24px',
    padding: '20px',
    background: 'rgba(255, 255, 255, 0.03)',
    borderRadius: '16px',
    border: '1px solid rgba(255, 255, 255, 0.1)'
  };

  const modernSectionTitleStyle = {
    margin: '0 0 16px 0',
    color: '#ffffff',
    fontSize: '18px',
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  };

  return (
    <form onSubmit={handleSubmit} style={{ padding: '0' }}>
      <style>{`
        .employee-form input:focus,
        .employee-form select:focus,
        .employee-form textarea:focus {
          outline: none;
          border-color: rgba(59, 130, 246, 0.5);
          background: rgba(255, 255, 255, 0.08);
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }
        .employee-form input::placeholder,
        .employee-form textarea::placeholder {
          color: rgba(255, 255, 255, 0.4);
        }
        .employee-form select {
          color: #ffffff;
          width: 100%;
          max-width: 100%;
          box-sizing: border-box;
          min-width: 0;
        }
        .employee-form select option {
          background: #1a1f3a;
          color: #ffffff;
        }
        .employee-form .select-wrapper {
          position: relative;
          overflow: visible;
          width: 100%;
          min-width: 0;
        }
        .employee-form-row > div,
        .employee-form .select-wrapper {
          overflow: visible;
          min-width: 0;
        }
        @media (max-width: 768px) {
          .employee-form .employee-form-row {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
      <div className="employee-form" style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <div className="employee-form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
        <div>
          <label style={modernLabelStyle}>
            {t('employees.first_name')} *
          </label>
          <input
            type="text"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            required
            placeholder={t('employees.first_name')}
            style={modernInputStyle}
          />
        </div>
        
        <div>
          <label style={modernLabelStyle}>
            {t('employees.last_name')} *
          </label>
          <input
            type="text"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            required
            placeholder={t('employees.last_name')}
            style={modernInputStyle}
          />
        </div>
      </div>

      <div className="employee-form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
        <div>
          <label style={modernLabelStyle}>
            {t('common.email')}
          </label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder={t('common.email')}
            style={modernInputStyle}
          />
        </div>
        
        <div>
          <label style={modernLabelStyle}>
            {t('common.phone')}
          </label>
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder={t('common.phone')}
            style={modernInputStyle}
          />
        </div>
      </div>

      <div className="employee-form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
        <div>
          <label style={modernLabelStyle}>
            {t('common.department')}
          </label>
          <CustomSelect
            name="department"
            value={formData.department}
            onChange={(val) => handleChange({ target: { name: 'department', value: val } })}
            placeholder={t('employees.select_department')}
            options={[
              { value: '', label: t('employees.select_department') },
              ...departments.filter(d => d).map(dept => {
                const predefined = DEPARTMENT_OPTIONS.find(o => o.value === dept);
                return { value: dept, label: predefined ? t(predefined.labelKey) : dept };
              }),
              ...DEPARTMENT_OPTIONS.filter(d => !departments.includes(d.value)).map(d => ({ value: d.value, label: t(d.labelKey) }))
            ]}
            style={modernInputStyle}
          />
        </div>
        
        <div>
          <label style={modernLabelStyle}>
            {t('common.position')}
          </label>
          <input
            type="text"
            name="position"
            value={formData.position}
            onChange={handleChange}
            placeholder={t('common.position')}
            style={modernInputStyle}
          />
        </div>
      </div>

      <div className="employee-form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
        <div>
          <label style={modernLabelStyle}>
            {t('employees.hire_date')}
          </label>
          <input
            type="date"
            name="hireDate"
            value={formData.hireDate}
            onChange={handleChange}
            style={modernInputStyle}
          />
        </div>
        
        <div>
          <label style={modernLabelStyle}>
            {t('employees.employment_type_label')}
          </label>
          <CustomSelect
            name="employmentType"
            value={formData.employmentType}
            onChange={(val) => handleChange({ target: { name: 'employmentType', value: val } })}
            placeholder={t('employees.select_placeholder')}
            options={[
              { value: '', label: t('employees.select_placeholder') },
              { value: 'CDI', label: t('employees.employment_cdi') },
              { value: 'CDD', label: t('employees.employment_cdd') },
              { value: 'Contractuel', label: t('employees.employment_contractuel') },
              { value: 'Permanent', label: t('employees.employment_permanent') },
              { value: 'Stage', label: t('employees.employment_stage') },
              { value: 'Interim', label: t('employees.employment_interim') }
            ]}
            style={modernInputStyle}
          />
        </div>
      </div>

      {/* 🚀 PHASE 1: Section Identification - Matricule et Code Employé */}
      <div style={modernSectionStyle}>
        <h4 style={modernSectionTitleStyle}>🔢 {t('employees.section_identification')}</h4>
        
        <div style={{ marginBottom: '16px' }}>
          <label style={modernLabelStyle}>
            {t('employees.matricule')} <small style={{ color: 'rgba(255, 255, 255, 0.5)', fontWeight: 'normal' }}>({t('employees.matricule_optional')})</small>
          </label>
          <input
            type="text"
            name="matricule"
            value={formData.matricule}
            onChange={handleChange}
            placeholder={t('employees.matricule_placeholder')}
            style={modernInputStyle}
          />
          <small style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '12px', display: 'block', marginTop: '6px' }}>
            {t('employees.matricule_help')}
          </small>
        </div>

        {!employee && (
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginBottom: '12px', color: 'rgba(255, 255, 255, 0.9)' }}>
              <input
                type="checkbox"
                name="useAutoCode"
                checked={formData.useAutoCode}
                onChange={(e) => {
                  setFormData(prev => ({
                    ...prev,
                    useAutoCode: e.target.checked,
                    employeeCode: e.target.checked ? '' : prev.employeeCode
                  }));
                }}
                style={{ transform: 'scale(1.2)', cursor: 'pointer' }}
              />
              <span style={{ fontWeight: 600 }}>{t('employees.generate_code_auto')}</span>
            </label>

            {!formData.useAutoCode && (
              <div>
                <label style={modernLabelStyle}>
                  {t('employees.code_manual')}
                </label>
                <input
                  type="text"
                  name="employeeCode"
                  value={formData.employeeCode}
                  onChange={handleChange}
                  required={!formData.useAutoCode}
                  placeholder={t('employees.code_placeholder')}
                  style={modernInputStyle}
                />
                <small style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '12px', display: 'block', marginTop: '6px' }}>
                  {t('employees.code_help')}
                </small>
              </div>
            )}

            {formData.useAutoCode && (
              <div style={{ padding: '12px', background: 'rgba(59, 130, 246, 0.15)', borderRadius: '12px', fontSize: '13px', color: '#93c5fd', border: '1px solid rgba(59, 130, 246, 0.3)' }}>
                ℹ️ {t('employees.code_auto_info')}
              </div>
            )}
          </div>
        )}

        {employee && (
          <div style={{ padding: '12px', background: 'rgba(245, 158, 11, 0.15)', borderRadius: '12px', fontSize: '13px', color: '#fbbf24', border: '1px solid rgba(245, 158, 11, 0.3)' }}>
            ℹ️ {t('employees.code_current_info')} <strong>{employee.employeeCode}</strong>
            <input
              type="text"
              name="employeeCode"
              value={formData.employeeCode}
              onChange={handleChange}
              placeholder={t('employees.leave_empty_to_keep')}
              style={{...modernInputStyle, marginTop: '8px', padding: '10px 14px'}}
            />
          </div>
        )}
      </div>

      {/* 🚀 PHASE 3: Section Rémunération */}
      <div style={modernSectionStyle}>
        <h4 style={modernSectionTitleStyle}>💰 {t('employees.section_remuneration')}</h4>
        
        <div className="employee-form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
          <div>
            <label style={modernLabelStyle}>
              {t('employees.salary_type_label')}
            </label>
            <CustomSelect
              name="salaryType"
              value={formData.salaryType}
              onChange={(val) => handleChange({ target: { name: 'salaryType', value: val } })}
              options={[
                { value: 'hourly', label: t('employees.salary_type_hourly') },
                { value: 'monthly', label: t('employees.salary_type_monthly') },
                { value: 'annual', label: t('employees.salary_type_annual') }
              ]}
              style={modernInputStyle}
            />
          </div>

          <div>
            <label style={modernLabelStyle}>
              {t('employees.currency_label')}
            </label>
            <CustomSelect
              name="salaryCurrency"
              value={formData.salaryCurrency}
              onChange={(val) => handleChange({ target: { name: 'salaryCurrency', value: val } })}
              options={[
                { value: 'EUR', label: t('employees.currency_eur') },
                { value: 'FCFA', label: t('employees.currency_fcfa') },
                { value: 'USD', label: t('employees.currency_usd') },
                { value: 'XOF', label: t('employees.currency_xof') }
              ]}
              style={modernInputStyle}
            />
          </div>
        </div>

        {formData.salaryType === 'hourly' && (
          <div>
            <label style={modernLabelStyle}>
              {t('employees.hourly_rate_label', { symbol: formData.salaryCurrency === 'EUR' ? '€' : formData.salaryCurrency === 'USD' ? '$' : 'FCFA' })}
            </label>
            <input
              type="number"
              step="0.01"
              name="hourlyRate"
              value={formData.hourlyRate}
              onChange={handleChange}
              placeholder={`Ex: ${formData.salaryCurrency === 'EUR' ? '15.50' : formData.salaryCurrency === 'USD' ? '18.00' : '10000'}`}
              style={modernInputStyle}
            />
          </div>
        )}

        {formData.salaryType === 'monthly' && (
          <div>
            <label style={modernLabelStyle}>
              {t('employees.salary_monthly_brut', { currency: formData.salaryCurrency === 'EUR' ? '€' : formData.salaryCurrency === 'USD' ? '$' : 'FCFA' })}
            </label>
            <input
              type="number"
              step="0.01"
              name="salaryMonthly"
              value={formData.salaryMonthly}
              onChange={handleChange}
              placeholder={`Ex: ${formData.salaryCurrency === 'EUR' ? '2500.00' : formData.salaryCurrency === 'USD' ? '3000.00' : '1500000'}`}
              style={modernInputStyle}
            />
          </div>
        )}

        {formData.salaryType === 'annual' && (
          <div>
            <label style={modernLabelStyle}>
              {t('employees.annual_salary_label')} ({formData.salaryCurrency === 'EUR' ? '€' : formData.salaryCurrency === 'USD' ? '$' : 'FCFA'}/an)
            </label>
            <input
              type="number"
              step="0.01"
              name="salaryMonthly"
              value={formData.salaryMonthly}
              onChange={handleChange}
              placeholder={`Ex: ${formData.salaryCurrency === 'EUR' ? '30000.00' : formData.salaryCurrency === 'USD' ? '36000.00' : '18000000'}`}
              style={modernInputStyle}
            />
          </div>
        )}
      </div>

      {/* 🚀 PHASE 2: Section Secteur Public */}
      <div style={modernSectionStyle}>
        <h4 style={modernSectionTitleStyle}>🏛️ {t('employees.section_public_sector')}</h4>
        
        <div className="employee-form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
          <div>
            <label style={modernLabelStyle}>
              {t('employees.administration')}
            </label>
            <input
              type="text"
              name="administration"
              value={formData.administration}
              onChange={handleChange}
              placeholder={t('employees.administration_placeholder')}
              style={modernInputStyle}
            />
          </div>

          <div>
            <label style={modernLabelStyle}>
              {t('employees.service_direction')}
            </label>
            <input
              type="text"
              name="service"
              value={formData.service}
              onChange={handleChange}
              placeholder={t('employees.service_placeholder')}
              style={modernInputStyle}
            />
          </div>
        </div>

        <div className="employee-form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px', marginBottom: '15px' }}>
          <div>
            <label style={modernLabelStyle}>
              {t('employees.bureau')}
            </label>
            <input
              type="text"
              name="bureau"
              value={formData.bureau}
              onChange={handleChange}
              placeholder={t('employees.bureau_placeholder')}
              style={modernInputStyle}
            />
          </div>

          <div>
            <label style={modernLabelStyle}>
              {t('employees.grade')}
            </label>
            <input
              type="text"
              name="grade"
              value={formData.grade}
              onChange={handleChange}
              placeholder={t('employees.grade_placeholder')}
              style={modernInputStyle}
            />
          </div>

          <div>
            <label style={modernLabelStyle}>
              {t('employees.echelle')}
            </label>
            <input
              type="text"
              name="echelle"
              value={formData.echelle}
              onChange={handleChange}
              placeholder={t('employees.echelle_placeholder')}
              style={modernInputStyle}
            />
          </div>
        </div>

        <div className="employee-form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px', marginBottom: '15px' }}>
          <div>
            <label style={modernLabelStyle}>
              {t('employees.indice')}
            </label>
            <input
              type="number"
              name="indice"
              value={formData.indice}
              onChange={handleChange}
              placeholder={t('employees.indice_placeholder')}
              style={modernInputStyle}
            />
          </div>

          <div>
            <label style={modernLabelStyle}>
              {t('employees.statut')}
            </label>
            <CustomSelect
              name="statut"
              value={formData.statut}
              onChange={(val) => handleChange({ target: { name: 'statut', value: val } })}
              placeholder={t('employees.statut_placeholder')}
              options={[
                { value: '', label: t('employees.statut_placeholder') },
                { value: 'titulaire', label: t('employees.statut_titulaire') },
                { value: 'contractuel', label: t('employees.statut_contractuel') },
                { value: 'stagiaire', label: t('employees.statut_stagiaire') },
                { value: 'vacataire', label: t('employees.statut_vacataire') }
              ]}
              style={modernInputStyle}
            />
          </div>

          <div>
            <label style={modernLabelStyle}>
              {t('employees.date_titularisation')}
            </label>
            <input
              type="date"
              name="dateTitularisation"
              value={formData.dateTitularisation}
              onChange={handleChange}
              style={modernInputStyle}
            />
          </div>
        </div>
      </div>

      {!employee && (
        <div style={{ 
          marginBottom: '20px', 
          padding: '20px', 
          background: 'rgba(59, 130, 246, 0.1)', 
          borderRadius: '12px', 
          border: '1px solid rgba(59, 130, 246, 0.2)',
          backdropFilter: 'blur(10px)'
        }}>
          <h4 style={{ 
            margin: '0 0 16px 0', 
            color: '#ffffff', 
            fontSize: '18px',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            🔐 {t('employees.user_account_section')}
          </h4>
          
          <div style={{ marginBottom: '16px' }}>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '10px', 
              cursor: 'pointer',
              color: 'rgba(255, 255, 255, 0.9)'
            }}>
              <input
                type="checkbox"
                name="createUserAccount"
                checked={formData.createUserAccount}
                onChange={handleChange}
                style={{ 
                  transform: 'scale(1.2)', 
                  cursor: 'pointer',
                  accentColor: colorPalette.primary
                }}
              />
              <span style={{ fontWeight: 500, fontSize: '15px' }}>{t('employees.create_user_account')}</span>
            </label>
          </div>

          {formData.createUserAccount && (
            <>
              {/* 🚀 NOUVEAU: Sélecteur de rôle */}
              <div className="select-wrapper" style={{ marginBottom: '16px' }}>
                <label style={modernLabelStyle}>
                  {t('employees.user_role_label')}
                </label>
                <CustomSelect
                  name="userRole"
                  value={formData.userRole}
                  onChange={(val) => handleChange({ target: { name: 'userRole', value: val } })}
                  options={[
                    { value: 'employee', label: `👤 ${t('employees.role_employee_option')}` },
                    { value: 'manager', label: `👨‍💼 ${t('employees.role_manager_option')}` },
                    { value: 'admin', label: `👑 ${t('employees.role_admin_option')}` }
                  ]}
                  style={modernInputStyle}
                />
                <small style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '12px', display: 'block', marginTop: '6px' }}>
                  {formData.userRole === 'employee' && t('employees.role_employee_permissions')}
                  {formData.userRole === 'manager' && t('employees.role_manager_permissions')}
                  {formData.userRole === 'admin' && t('employees.role_admin_permissions')}
                </small>
              </div>

              <div>
                <label style={modernLabelStyle}>
                  {t('employees.temporary_password_label')}
                </label>
                <input
                  type="text"
                  name="userPassword"
                  value={formData.userPassword}
                  onChange={handleChange}
                  style={modernInputStyle}
                />
                <small style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '12px', display: 'block', marginTop: '6px' }}>
                  {t('employees.temporary_password_help')}
                </small>
              </div>
            </>
          )}
        </div>
      )}

      <div style={{ marginBottom: '20px' }}>
        <label style={modernLabelStyle}>
          {t('employees.notes_label_form')}
        </label>
        <textarea
          name="notes"
          value={formData.notes}
          onChange={handleChange}
          rows="3"
          style={{
            ...modernInputStyle,
            resize: 'vertical',
            minHeight: '80px'
          }}
          placeholder={t('employees.notes_placeholder_form')}
        />
      </div>

      <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '20px' }}>
        <button
          type="button"
          onClick={onCancel}
          style={{
            padding: '12px 24px',
            border: '1px solid rgba(255, 255, 255, 0.4)',
            borderRadius: '8px',
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
            color: '#ffffff',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            transition: 'all 0.2s ease',
            backdropFilter: 'blur(10px)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.15)';
            e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.6)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.08)';
            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.4)';
          }}
        >
          {t('common.cancel') || 'Annuler'}
        </button>
        <button
          type="submit"
          disabled={loading}
          style={{
            padding: '12px 24px',
            border: 'none',
            borderRadius: '8px',
            background: colorPalette.equipe.gradient,
            color: '#fff',
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.6 : 1,
            fontSize: '14px',
            fontWeight: '600',
            transition: 'all 0.2s ease',
            boxShadow: loading ? 'none' : `0 4px 16px ${colorPalette.equipe.shadow}`
          }}
          onMouseEnter={(e) => {
            if (!loading) {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = `0 6px 20px ${colorPalette.equipe.shadow}`;
            }
          }}
          onMouseLeave={(e) => {
            if (!loading) {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = `0 4px 16px ${colorPalette.equipe.shadow}`;
            }
          }}
        >
          {loading ? t('common.loading') : employee ? t('common.edit') : t('common.add')}
        </button>
      </div>
      </div>
    </form>
  );
};

// Composant principal Employees
const Employees = ({ onNavigate }) => {
  const { t, language } = useTranslation();
  const exportLang = language === 'fr' ? 'fr' : 'en';
  // ✅ Utiliser le hook useAuth() au lieu de la logique personnalisée
  const { user: currentUser, loading: userLoading } = useAuth();
  
  // État principal
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // États des modales
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  
  // Import/Export & selection
  const [selectedIds, setSelectedIds] = useState([]);
  const [exportScope, setExportScope] = useState('all');
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  // États des filtres — active=all: saare employees (active + inactive) by default
  const [filters, setFilters] = useState({
    search: '',
    department: '',
    active: 'all'
  });

  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 768);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Calculer les permissions
  const canManageEmployees = currentUser && ['admin', 'manager'].includes(currentUser.role);
  const canDeletePermanently = currentUser && currentUser.role === 'admin';

  // Charger les employés (15s timeout)
  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams(filters);
      const response = await fetchWithTimeout(
        `${API_BASE}/api/employees?${params}`,
        { headers: { 'Authorization': `Bearer ${token}` } },
        15000
      );
      const data = await response.json();
      
      if (data.success) {
        // ✅ CORRECTION : Gérer la structure paginée (data.employees ou data.data)
        const employeesList = data.employees || data.data || [];
        setEmployees(employeesList);
        setDepartments(data.departments || []);
      } else {
        setError(data.error || t('employees.error_loading'));
        setEmployees([]);
        setDepartments([]);
      }
    } catch (error) {
      console.error('❌ Erreur chargement employés:', error);
      setError(t('employees.error_connection'));
      setEmployees([]);
      setDepartments([]);
    }
  };

  // Charger les statistiques (15s timeout)
  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetchWithTimeout(
        `${API_BASE}/api/employees/stats`,
        { headers: { 'Authorization': `Bearer ${token}` } },
        15000
      );
      const data = await response.json();
      if (data.success) {
        setStats(data.overview);
      }
    } catch (error) {
      console.error('❌ Erreur chargement stats:', error);
    }
  };

  // Créer un nouvel employé
  const handleCreateEmployee = async (employeeData) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/employees`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(employeeData)
      });

      const data = await response.json();
      
      if (data.success) {
        setSuccess(`Employé créé avec succès ! Code: ${data.employee.employeeCode}`);
        if (data.credentials) {
          setSuccess(prev => prev + ` - Identifiants: ${data.credentials.email} / ${data.credentials.password}`);
        }
        await fetchEmployees();
        await fetchStats();
        return true;
      } else {
        setError(data.error || t('employees.error_create'));
        return false;
      }
    } catch (error) {
      console.error('❌ Erreur création:', error);
      setError(t('employees.error_connection'));
      return false;
    }
  };

  // Modifier un employé
  const handleUpdateEmployee = async (employeeData) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/employees/${editingEmployee.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(employeeData)
      });

      const data = await response.json();
      
      if (data.success) {
        setSuccess('Employé modifié avec succès !');
        await fetchEmployees();
        await fetchStats();
        setEditingEmployee(null);
        return true;
      } else {
        setError(data.error || t('employees.error_update'));
        return false;
      }
    } catch (error) {
      console.error('❌ Erreur modification:', error);
      setError(t('employees.error_connection'));
      return false;
    }
  };

  // Supprimer/Désactiver un employé
  const handleDeleteEmployee = async (employee, permanent = false) => {
    if (permanent && !canDeletePermanently) {
      setError(t('employees.only_admin_delete'));
      return;
    }

    const action = permanent ? t('employees.delete_permanent') : t('employees.deactivate');
    if (!window.confirm(t('employees.delete_confirm', { name: `${employee.firstName} ${employee.lastName}` }).replace('{{name}}', `${employee.firstName} ${employee.lastName}`))) {
      return;
    }

    if (permanent) {
      const confirmText = (window.prompt(t('employees.delete_permanent_confirm')) || '').trim().toUpperCase();
      if (confirmText !== 'SUPPRIMER' && confirmText !== 'DELETE') {
        return;
      }
    }

    try {
      setError('');
      const token = localStorage.getItem('token');
      const empId = employee.id || employee._id;
      if (!empId) {
        setError(t('employees.error_delete') || 'Employee ID missing');
        return;
      }
      const response = await fetch(`${API_BASE}/api/employees/${empId}?permanent=${permanent}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json().catch(() => ({}));
      
      if (response.ok && data.success) {
        setSuccess(data.message || (permanent ? 'Employee deleted.' : 'Employee deactivated.'));
        setError('');
        await fetchEmployees();
        await fetchStats();
      } else {
        setError(data.error || t('employees.error_delete') || 'Delete failed');
      }
    } catch (error) {
      console.error(`❌ Erreur ${action}:`, error);
      setError(t('employees.error_connection') || 'Connection error');
    }
  };

  // Réactiver un employé
  const handleReactivateEmployee = async (employee) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/employees/${employee.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ ...employee, isActive: true })
      });

      const data = await response.json();
      
      if (data.success) {
        setSuccess('Employé réactivé avec succès !');
        await fetchEmployees();
        await fetchStats();
      } else {
        setError(data.error || t('employees.error_reactivate'));
      }
    } catch (error) {
      console.error('❌ Erreur réactivation:', error);
      setError(t('employees.error_connection'));
    }
  };

  // Export to Excel (scope: 'all' | 'selected')
  const handleExport = async (scope) => {
    const useScope = scope !== undefined ? scope : exportScope;
    setExporting(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const url = useScope === 'all'
        ? `${API_BASE}/api/employees/export?all=true&lang=${exportLang}`
        : `${API_BASE}/api/employees/export?ids=${(selectedIds || []).join(',')}&lang=${exportLang}`;
      const response = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        setError(data.error || `Export failed: ${response.status}`);
        return;
      }
      const blob = await response.blob();
      const disposition = response.headers.get('Content-Disposition');
      const filename = (disposition && disposition.match(/filename="?([^";]+)"?/)?.[1]) || `employees-export-${new Date().toISOString().slice(0, 10)}.xlsx`;
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
      setExportDropdownOpen(false);
    } catch (err) {
      console.error('Export error:', err);
      setError(t('employees.export_error') || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const toggleSelectEmployee = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const toggleSelectAll = () => {
    if (!employees?.length) return;
    const allIds = employees.map(e => e.id);
    const allSelected = allIds.every(id => selectedIds.includes(id));
    setSelectedIds(allSelected ? [] : allIds);
  };

  // Chargement initial
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        await Promise.all([fetchEmployees(), fetchStats()]);
      } catch (e) {
        setError(t('employees.error_server_connection'));
      } finally {
        setLoading(false);
      }
    };

    if (currentUser && canManageEmployees) {
      loadData();
    } else if (!userLoading) {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, filters, canManageEmployees, userLoading]);

  // Gestion des messages
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // Affichage en cours de chargement
  if (userLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        width: '100%',
        background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 100%)',
        color: '#ffffff',
        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
        overflowX: 'hidden',
        paddingBottom: 'env(safe-area-inset-bottom, 0)',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflowY: 'auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ fontSize: '18px', color: 'rgba(255, 255, 255, 0.7)', textAlign: 'center' }}>
          {t('employees.loading_permissions')}
        </div>
      </div>
    );
  }

  // Vérification des permissions
  if (!canManageEmployees) {
    return (
      <div style={{
        minHeight: '100vh',
        width: '100%',
        background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 100%)',
        color: '#ffffff',
        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
        overflowX: 'hidden',
        paddingBottom: 'env(safe-area-inset-bottom, 0)',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflowY: 'auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <div style={{
          textAlign: 'center',
          padding: '60px',
          background: 'rgba(239, 68, 68, 0.2)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '16px',
          maxWidth: '500px'
        }}>
          <XCircle size={48} style={{ marginBottom: '20px', color: '#ef4444' }} />
          <h3 style={{ color: '#ffffff', marginBottom: '12px', fontSize: '24px', fontWeight: 700 }}>
            Accès refusé
          </h3>
          <p style={{ color: 'rgba(255, 255, 255, 0.8)', marginBottom: '8px', fontSize: '16px' }}>
            {t('employees.access_denied') || 'Vous n\'avez pas les permissions nécessaires pour gérer les employés.'}
          </p>
          <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '14px' }}>
            Contactez votre administrateur pour obtenir les droits appropriés.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
    <div style={{ 
      minHeight: '100vh',
      width: '100%',
      background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 100%)',
      color: '#ffffff',
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
      overflowX: 'hidden',
      paddingBottom: 'env(safe-area-inset-bottom, 0)',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      overflowY: 'auto'
    }}>
      {/* Header fixe - responsive: drawer on mobile */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        padding: isMobile ? '16px 16px 12px' : '24px 20px 20px',
        background: 'transparent',
        backdropFilter: 'none',
        borderBottom: 'none',
        zIndex: 999,
        maxWidth: '100%',
        boxSizing: 'border-box'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '12px',
          maxWidth: '100%',
          minWidth: 0
        }}>
          {isMobile ? (
            <>
              <div style={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                overflow: 'hidden'
              }}>
                <Users size={28} style={{ flexShrink: 0 }} />
                <span style={{
                  fontSize: '22px',
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>{t('employees.title')}</span>
              </div>
              <button
                type="button"
                onClick={() => setDrawerOpen(true)}
                aria-label={t('home.open_menu') || 'Menu'}
                title={t('home.open_menu') || 'Menu'}
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  color: '#ffffff',
                  padding: '10px',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  width: '44px',
                  height: '44px'
                }}
              >
                <PanelRightOpen size={20} />
              </button>
            </>
          ) : (
            <>
              {onNavigate && (
                <button
                  onClick={() => onNavigate('home')}
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    color: '#ffffff',
                    padding: '10px',
                    borderRadius: '12px',
                    fontSize: '18px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s',
                    backdropFilter: 'blur(10px)',
                    flexShrink: 0,
                    width: '44px',
                    height: '44px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                    e.currentTarget.style.transform = 'scale(1.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                  title={t('employees.back_to_home_title')}
                >
                  <Home size={20} />
                </button>
              )}
              <div style={{
                fontSize: 'clamp(28px, 5vw, 40px)',
                fontWeight: 700,
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                minWidth: 0
              }}>
                <Users size={32} />
                {t('employees.title')}
              </div>
              {/* Language selector in top bar (large screen only) - before blue button to avoid overlap */}
              <div style={{ flexShrink: 0 }}>
                <LanguageSelector
                  style={{
                    background: 'rgba(255, 255, 255, 0.08)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    padding: '8px 12px',
                    borderRadius: 12,
                    backdropFilter: 'blur(10px)'
                  }}
                />
              </div>
              {canManageEmployees && (
                <>
                  <button
                    onClick={() => onNavigate && onNavigate('/employees/import')}
                    style={{
                      padding: '12px 20px',
                      background: 'rgba(255,255,255,0.12)',
                      color: 'white',
                      border: '1px solid rgba(255,255,255,0.3)',
                      borderRadius: '12px',
                      fontSize: '14px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      flexShrink: 0
                    }}
                  >
                    <FileUp size={18} />
                    {t('employees.add_with_csv') || 'Add with CSV'}
                  </button>
                  <div style={{ position: 'relative', flexShrink: 0 }}>
                    <button
                      onClick={() => setExportDropdownOpen(prev => !prev)}
                      style={{
                        padding: '12px 20px',
                        background: 'rgba(255,255,255,0.12)',
                        color: 'white',
                        border: '1px solid rgba(255,255,255,0.3)',
                        borderRadius: '12px',
                        fontSize: '14px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      <Download size={18} />
                      {t('employees.export_employees') || 'Export employees'}
                      <ChevronDown size={16} style={{ opacity: 0.8 }} />
                    </button>
                    {exportDropdownOpen && (
                      <>
                        <div style={{ position: 'fixed', inset: 0, zIndex: 10 }} onClick={() => setExportDropdownOpen(false)} />
                        <div style={{
                          position: 'absolute', top: '100%', right: 0, marginTop: '6px',
                          background: 'linear-gradient(180deg, #1a1f3a 0%, #0f1322 100%)',
                          border: '1px solid rgba(255,255,255,0.2)',
                          borderRadius: '12px',
                          boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                          zIndex: 11,
                          minWidth: '200px',
                          overflow: 'hidden'
                        }}>
                          <button
                            onClick={() => { setExportScope('all'); setExportDropdownOpen(false); handleExport('all'); }}
                            disabled={exporting}
                            style={{
                              width: '100%', padding: '12px 16px', textAlign: 'left',
                              background: exportScope === 'all' ? 'rgba(59,130,246,0.2)' : 'transparent',
                              border: 'none', color: '#fff', cursor: exporting ? 'not-allowed' : 'pointer', fontSize: '14px'
                            }}
                          >
                            {t('employees.export_all') || 'All employees'}
                          </button>
                          <button
                            onClick={() => { setExportScope('selected'); setExportDropdownOpen(false); handleExport('selected'); }}
                            disabled={exporting || (selectedIds || []).length === 0}
                            style={{
                              width: '100%', padding: '12px 16px', textAlign: 'left',
                              background: exportScope === 'selected' ? 'rgba(59,130,246,0.2)' : 'transparent',
                              border: 'none', color: (selectedIds || []).length === 0 ? 'rgba(255,255,255,0.4)' : '#fff',
                              cursor: (exporting || (selectedIds || []).length === 0) ? 'not-allowed' : 'pointer',
                              fontSize: '14px'
                            }}
                          >
                            {t('employees.export_selected') || 'Selected'} ({(selectedIds || []).length})
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                  <button
                    onClick={() => setShowCreateModal(true)}
                    style={{
                      padding: '12px 24px',
                      background: colorPalette.equipe.gradient,
                      color: 'white',
                      border: 'none',
                      borderRadius: '12px',
                      fontSize: '15px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      boxShadow: `0 4px 16px ${colorPalette.equipe.shadow}`,
                      flexShrink: 0
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = `0 6px 20px ${colorPalette.equipe.shadow}`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = `0 4px 16px ${colorPalette.equipe.shadow}`;
                    }}
                  >
                    <Plus size={18} />
                    {t('employees.add_employee')}
                  </button>
                </>
              )}
            </>
          )}
        </div>
        {!isMobile && (
          <p style={{
            fontSize: 'clamp(14px, 2vw, 16px)',
            color: 'rgba(255, 255, 255, 0.7)',
            margin: '8px 0 0 0',
            letterSpacing: '0.2px',
            fontWeight: 500
          }}>
            {t('employees.subtitle') || 'Gérez votre équipe efficacement'}
          </p>
        )}
      </div>

      {/* Mobile Drawer */}
      {isMobile && drawerOpen && (
        <>
          <div
            onClick={() => setDrawerOpen(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.5)',
              zIndex: 1300,
              animation: 'fadeIn 0.2s ease-out'
            }}
          />
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            bottom: 0,
            width: '280px',
            maxWidth: '85vw',
            background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 100%)',
            borderRight: '1px solid rgba(255,255,255,0.1)',
            zIndex: 1301,
            padding: '24px 16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
            boxShadow: '4px 0 24px rgba(0,0,0,0.3)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '18px', fontWeight: 700 }}>{t('employees.title')}</span>
              <button
                onClick={() => setDrawerOpen(false)}
                style={{
                  background: 'rgba(255,255,255,0.1)',
                  border: 'none',
                  color: '#fff',
                  width: 36,
                  height: 36,
                  borderRadius: 8,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <XCircle size={20} />
              </button>
            </div>
            {onNavigate && (
              <button
                onClick={() => { onNavigate('home'); setDrawerOpen(false); }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '12px 16px',
                  background: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: 12,
                  color: '#fff',
                  fontSize: 15,
                  fontWeight: 500,
                  cursor: 'pointer',
                  width: '100%'
                }}
              >
                <Home size={20} />
                {t('common.back_to_home')}
              </button>
            )}
            {canManageEmployees && (
              <button
                onClick={() => { setShowCreateModal(true); setDrawerOpen(false); }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '12px 16px',
                  background: colorPalette.equipe.gradient,
                  border: 'none',
                  borderRadius: 12,
                  color: 'white',
                  fontSize: 15,
                  fontWeight: 600,
                  cursor: 'pointer',
                  width: '100%',
                  boxShadow: `0 4px 16px ${colorPalette.equipe.shadow}`
                }}
              >
                <Plus size={20} />
                {t('employees.add_employee')}
              </button>
            )}
            <div style={{ marginTop: 'auto', paddingTop: 16 }}>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 8 }}>{t('common.language') || 'Language'}</div>
              <LanguageSelector style={{ width: '100%' }} />
            </div>
          </div>
        </>
      )}

      {/* Contenu principal */}
      <div style={{
        paddingTop: '140px',
        paddingBottom: '40px',
        paddingLeft: '20px',
        paddingRight: '20px',
        maxWidth: '1400px',
        margin: '0 auto'
      }}>
      {/* Messages */}
      {error && (
        <ModernCard
          style={{
            background: 'rgba(239, 68, 68, 0.2)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            marginBottom: '20px'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', color: '#ef4444' }}>
            <XCircle size={20} />
            <span style={{ fontWeight: 600 }}>{error}</span>
          </div>
        </ModernCard>
      )}

      {success && (
        <ModernCard
          style={{
            background: 'rgba(16, 185, 129, 0.2)',
            border: '1px solid rgba(16, 185, 129, 0.3)',
            marginBottom: '20px'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', color: '#10b981' }}>
            <CheckCircle size={20} />
            <span style={{ fontWeight: 600 }}>{success}</span>
          </div>
        </ModernCard>
      )}

      {/* Statistiques */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
        gap: '20px', 
        marginBottom: '24px' 
      }}>
        <ModernCard
          gradient={colorPalette.equipe.gradient}
          shadow={colorPalette.equipe.shadow}
          style={{ textAlign: 'center' }}
        >
          <div style={{ fontSize: '36px', fontWeight: 700, marginBottom: '8px', color: '#ffffff' }}>
            {stats.total || 0}
          </div>
          <div style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.9)', fontWeight: 500 }}>
            {t('employees.total_employees')}
          </div>
        </ModernCard>
        
        <ModernCard
          gradient={colorPalette.profil.gradient}
          shadow={colorPalette.profil.shadow}
          style={{ textAlign: 'center' }}
        >
          <div style={{ fontSize: '36px', fontWeight: 700, marginBottom: '8px', color: '#ffffff' }}>
            {stats.with_user_account || 0}
          </div>
          <div style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.9)', fontWeight: 500 }}>
            {t('employees.with_account')}
          </div>
        </ModernCard>
        
        <ModernCard
          gradient={colorPalette.warning?.gradient || 'linear-gradient(135deg, #f59e0b, #fbbf24)'}
          shadow={colorPalette.warning?.shadow || 'rgba(245, 158, 11, 0.3)'}
          style={{ textAlign: 'center' }}
        >
          <div style={{ fontSize: '36px', fontWeight: 700, marginBottom: '8px', color: '#ffffff' }}>
            {(stats.total || 0) - (stats.with_user_account || 0)}
          </div>
          <div style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.9)', fontWeight: 500 }}>
            {t('employees.without_account')}
          </div>
        </ModernCard>
        
        <ModernCard
          gradient={colorPalette.stats.gradient}
          shadow={colorPalette.stats.shadow}
          style={{ textAlign: 'center' }}
        >
          <div style={{ fontSize: '36px', fontWeight: 700, marginBottom: '8px', color: '#ffffff' }}>
            {stats.departments_count || 0}
          </div>
          <div style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.9)', fontWeight: 500 }}>
            {t('common.department')}
          </div>
        </ModernCard>
      </div>

      <p style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.55)', margin: '-8px 0 16px 0' }}>
        {t('employees.stats_scope_info')}
      </p>

      {/* Filtres */}
      <ModernCard
        title="Filtres"
        icon={<Filter size={20} />}
        style={{ marginBottom: '24px' }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: 600,
              color: 'rgba(255, 255, 255, 0.9)',
              fontSize: '14px'
            }}>
              <Search size={14} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
              {t('common.search')}
            </label>
            <input
              type="text"
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              placeholder={t('employees.search_placeholder') || 'Nom, email, code...'}
              style={{
                width: '100%',
                padding: '12px',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '12px',
                color: '#ffffff',
                fontSize: '14px',
                backdropFilter: 'blur(10px)'
              }}
            />
          </div>
          
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: 600,
              color: 'rgba(255, 255, 255, 0.9)',
              fontSize: '14px'
            }}>
              <Building size={14} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
              {t('common.department')}
            </label>
            <select
              value={filters.department}
              onChange={(e) => setFilters(prev => ({ ...prev, department: e.target.value }))}
              style={{
                width: '100%',
                padding: '12px',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '12px',
                color: '#ffffff',
                fontSize: '14px',
                backdropFilter: 'blur(10px)'
              }}
            >
              <option value="" style={{ background: '#1a1f3a', color: '#ffffff' }}>{t('employees.all_departments') || 'Tous les départements'}</option>
              {(departments || []).map(dept => (
                <option key={dept} value={dept} style={{ background: '#1a1f3a', color: '#ffffff' }}>{dept}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: 600,
              color: 'rgba(255, 255, 255, 0.9)',
              fontSize: '14px'
            }}>
              {t('common.status')}
            </label>
            <select
              value={filters.active}
              onChange={(e) => setFilters(prev => ({ ...prev, active: e.target.value }))}
              style={{
                width: '100%',
                padding: '12px',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '12px',
                color: '#ffffff',
                fontSize: '14px',
                backdropFilter: 'blur(10px)'
              }}
            >
              <option value="true" style={{ background: '#1a1f3a', color: '#ffffff' }}>{t('employees.active_only') || 'Actifs seulement'}</option>
              <option value="false" style={{ background: '#1a1f3a', color: '#ffffff' }}>{t('employees.inactive_only') || 'Inactifs seulement'}</option>
              <option value="all" style={{ background: '#1a1f3a', color: '#ffffff' }}>{t('common.all') || 'Tous'}</option>
            </select>
          </div>
        </div>
      </ModernCard>

      {/* Liste des employés */}
      {loading ? (
        <ModernCard style={{ textAlign: 'center', padding: '60px' }}>
          <div style={{ fontSize: '18px', color: 'rgba(255, 255, 255, 0.7)' }}>{t('employees.loading_employees')}</div>
        </ModernCard>
      ) : !employees || employees.length === 0 ? (
        <ModernCard style={{ textAlign: 'center', padding: '60px' }}>
          <Users size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
          <h3 style={{ color: '#ffffff', marginBottom: '8px' }}>{t('employees.no_employees_found')}</h3>
          <p style={{ color: 'rgba(255, 255, 255, 0.7)' }}>{t('employees.create_first_employee')}</p>
        </ModernCard>
      ) : (
        <>
          {canManageEmployees && (employees || []).length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'rgba(255,255,255,0.9)', cursor: 'pointer', fontSize: '14px' }}>
                <input
                  type="checkbox"
                  checked={(employees || []).length > 0 && (employees || []).every(e => selectedIds.includes(e.id))}
                  onChange={toggleSelectAll}
                  style={{ width: '18px', height: '18px', accentColor: '#3b82f6' }}
                />
                {t('employees.select_all') || 'Select all'}
              </label>
              {(selectedIds || []).length > 0 && (
                <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px' }}>
                  {(selectedIds || []).length} {t('employees.selected') || 'selected'}
                </span>
              )}
            </div>
          )}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', 
            gap: '20px' 
          }}>
          {(employees || []).map(employee => (
            <ModernCard
              key={employee.id}
              style={{ cursor: 'default' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                {canManageEmployees && (
                  <label style={{ flexShrink: 0, cursor: 'pointer', marginRight: '8px' }} onClick={e => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(employee.id)}
                      onChange={() => toggleSelectEmployee(employee.id)}
                      style={{ width: '18px', height: '18px', accentColor: '#3b82f6' }}
                    />
                  </label>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h3 style={{ 
                    fontSize: '18px', 
                    fontWeight: 700, 
                    margin: '0 0 8px 0',
                    color: '#ffffff',
                    letterSpacing: '-0.3px'
                  }}>
                    {employee.firstName} {employee.lastName}
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <ModernBadge variant="info" style={{ width: 'fit-content' }}>
                      {employee.employeeCode}
                    </ModernBadge>
                    {employee.matricule && (
                      <ModernBadge variant="warning" style={{ width: 'fit-content' }}>
                        {employee.matricule}
                      </ModernBadge>
                    )}
                  </div>
                </div>
                <ModernBadge variant={employee.isActive ? 'success' : 'danger'}>
                  {employee.isActive ? t('employees.status_active') : t('employees.status_inactive')}
                </ModernBadge>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px' }}>
                {employee.email || employee.userEmail ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'rgba(255, 255, 255, 0.8)' }}>
                    <Mail size={16} />
                    <span style={{ fontSize: '14px' }}>{employee.email || employee.userEmail}</span>
                  </div>
                ) : null}
                
                {employee.department && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'rgba(255, 255, 255, 0.8)' }}>
                    <Building size={16} />
                    <span style={{ fontSize: '14px' }}>{employee.department}</span>
                  </div>
                )}
                
                {employee.position && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'rgba(255, 255, 255, 0.8)' }}>
                    <Users size={16} />
                    <span style={{ fontSize: '14px' }}>{employee.position}</span>
                  </div>
                )}

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {employee.hasUserAccount ? (
                    <ModernBadge variant="success" icon={<CheckCircle size={14} />}>
                      {t('employees.account_active')}
                      {employee.userRole && (
                        <span style={{ marginLeft: '6px', fontSize: '12px' }}>
                          {employee.userRole === 'admin' ? '👑' : employee.userRole === 'manager' ? '👨‍💼' : '👤'}
                        </span>
                      )}
                    </ModernBadge>
                  ) : (
                    <ModernBadge variant="danger" icon={<XCircle size={14} />}>
                      {t('employees.no_account')}
                    </ModernBadge>
                  )}
                </div>
              </div>

              {canManageEmployees && (
                <div style={{ 
                  display: 'flex', 
                  gap: '8px', 
                  paddingTop: '16px',
                  borderTop: '1px solid rgba(255, 255, 255, 0.1)'
                }}>
                  <button
                    onClick={() => setEditingEmployee(employee)}
                    style={{
                      flex: 1,
                      padding: '12px',
                      background: colorPalette.equipe.gradient,
                      color: 'white',
                      border: 'none',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      boxShadow: `0 4px 12px ${colorPalette.equipe.shadow}`
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = `0 6px 16px ${colorPalette.equipe.shadow}`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = `0 4px 12px ${colorPalette.equipe.shadow}`;
                    }}
                  >
                    <Edit size={16} />
                    {t('employees.modify')}
                  </button>
                  
                  {employee.isActive ? (
                    <button
                      onClick={() => handleDeleteEmployee(employee, false)}
                      style={{
                        padding: '12px',
                        background: 'rgba(245, 158, 11, 0.2)',
                        color: '#f59e0b',
                        border: '1px solid rgba(245, 158, 11, 0.3)',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: 600,
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        backdropFilter: 'blur(10px)',
                        boxShadow: '0 2px 8px rgba(245, 158, 11, 0.2)'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(245, 158, 11, 0.3)';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(245, 158, 11, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(245, 158, 11, 0.2)';
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(245, 158, 11, 0.2)';
                      }}
                    >
                      {t('employees.deactivate')}
                    </button>
                  ) : (
                    <>
                      {canManageEmployees && (
                        <button
                          onClick={() => handleReactivateEmployee(employee)}
                          style={{
                            padding: '12px',
                            background: 'rgba(16, 185, 129, 0.2)',
                            color: '#10b981',
                            border: '1px solid rgba(16, 185, 129, 0.3)',
                            borderRadius: '12px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: 600,
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            backdropFilter: 'blur(10px)',
                            boxShadow: '0 2px 8px rgba(16, 185, 129, 0.2)'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'rgba(16, 185, 129, 0.3)';
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'rgba(16, 185, 129, 0.2)';
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 2px 8px rgba(16, 185, 129, 0.2)';
                          }}
                        >
                          {t('employees.reactivate')}
                        </button>
                      )}
                      
                      {canDeletePermanently && (
                        <button
                          onClick={() => handleDeleteEmployee(employee, true)}
                          style={{
                            padding: '12px',
                            background: 'rgba(239, 68, 68, 0.2)',
                            color: '#ef4444',
                            border: '1px solid rgba(239, 68, 68, 0.3)',
                            borderRadius: '12px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: 600,
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            backdropFilter: 'blur(10px)',
                            boxShadow: '0 2px 8px rgba(239, 68, 68, 0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'rgba(239, 68, 68, 0.3)';
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.3)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)';
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 2px 8px rgba(239, 68, 68, 0.2)';
                          }}
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}
            </ModernCard>
          ))}
          </div>
        </>
      )}
      </div>
    </div>

      {/* Modal de création - Design moderne */}
      {showCreateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          backdropFilter: 'blur(10px)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
          overflowY: 'auto',
          padding: '16px'
        }}>
          <div style={{
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '20px',
            width: 'min(95%, 800px)',
            maxWidth: '800px',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.05)',
            margin: 'auto'
          }}>
            <div style={{
              background: 'linear-gradient(135deg, #2196f3, #64b5f6)',
              color: 'white',
              padding: 'clamp(16px, 4vw, 24px)',
              borderRadius: '20px 20px 0 0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              position: 'sticky',
              top: 0,
              zIndex: 10
            }}>
              <h2 style={{ color: 'white', margin: 0, fontSize: 'clamp(18px, 4vw, 24px)', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
                <Plus size={24} style={{ flexShrink: 0 }} />
                {t('employees.new_employee')}
              </h2>
              <button
                onClick={() => setShowCreateModal(false)}
                style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: 'none',
                  color: 'white',
                  fontSize: '24px',
                  cursor: 'pointer',
                  width: '36px',
                  height: '36px',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
                  e.currentTarget.style.transform = 'scale(1.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                ×
              </button>
            </div>
            <div style={{ padding: 'clamp(16px, 4vw, 24px)' }}>
              <EmployeeForm
                onSave={handleCreateEmployee}
                onCancel={() => setShowCreateModal(false)}
                departments={departments}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal de modification - Design moderne */}
      {editingEmployee && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          backdropFilter: 'blur(10px)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
          overflowY: 'auto',
          padding: '16px'
        }}>
          <div style={{
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '20px',
            width: 'min(95%, 800px)',
            maxWidth: '800px',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.05)',
            margin: 'auto'
          }}>
            <div style={{
              background: 'linear-gradient(135deg, #2196f3, #64b5f6)',
              color: 'white',
              padding: 'clamp(16px, 4vw, 24px)',
              borderRadius: '20px 20px 0 0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              position: 'sticky',
              top: 0,
              zIndex: 10
            }}>
              <h2 style={{ color: 'white', margin: 0, fontSize: 'clamp(18px, 4vw, 24px)', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
                <Edit size={24} style={{ flexShrink: 0 }} />
                {t('employees.edit_employee')}
              </h2>
              <button
                onClick={() => setEditingEmployee(null)}
                style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: 'none',
                  color: 'white',
                  fontSize: '24px',
                  cursor: 'pointer',
                  width: '36px',
                  height: '36px',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
                  e.currentTarget.style.transform = 'scale(1.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                ×
              </button>
            </div>
            <div style={{ padding: 'clamp(16px, 4vw, 24px)' }}>
              <EmployeeForm
                employee={editingEmployee}
                onSave={handleUpdateEmployee}
                onCancel={() => setEditingEmployee(null)}
                departments={departments}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Employees;